// src/hooks/useQuoteActivity.js
// SEAM 3 VERIFIER: qact-3b7e9d52-hook   (extracted in Seam 6, qfup-7d3e9a41)
//
// ONE live subscription to a quote's activity log, shared by every surface that
// renders it. Lifted out of QuoteActivityTimeline in Seam 6, when the follow-up
// strip became a second reader: two components each owning an onSnapshot over
// the SAME 50 documents would be billed twice, forever, for identical data.
//
// Data: organizations/{orgId}/quotes/{quoteId}/activity, written ONLY by Cloud
// Functions (functions/utils/quoteActivity.js). Portal customers cannot read it
// (firestore.rules), so nothing built on this may be mounted in a portal view.
//
// ⚠️ THE CAP IS A CORRECTNESS BOUNDARY, NOT JUST A COST ONE. This reads the
// MAX_ROWS most recent entries of ALL types. Any consumer that filters by type
// is therefore showing "entries of that type among the most recent MAX_ROWS" —
// which, on a busy estimate, is not the same as "all entries of that type".
// Consumers that filter MUST say so when they are truncating; the follow-up
// strip compares its row count against the server-authoritative
// quote.followUpCount and tells the user rather than silently showing fewer.
//
// A type-filtered Firestore query (where('type','==',…) + orderBy('at','desc'))
// would remove that boundary, but needs a composite index — and this repo has
// no firestore.indexes.json, so indexes exist only in the Console (logged in
// BACKLOG as its own gap). Revisit when any org's busiest quote nears MAX_ROWS;
// production on 2026-09-18 peaked at 5 entries on a single estimate.
import { useEffect, useState } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

export const MAX_ROWS = 50;

export default function useQuoteActivity(orgId, quoteId) {
    const [entries, setEntries] = useState([]);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        if (!orgId || !quoteId) {
            setEntries([]);
            setLoadError(false);
            return undefined;
        }
        setLoadError(false);
        const q = query(
            collection(db, `organizations/${orgId}/quotes/${quoteId}/activity`),
            orderBy('at', 'desc'),
            limit(MAX_ROWS)
        );
        const unsub = onSnapshot(
            q,
            (snap) => setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (err) => {
                console.warn('useQuoteActivity: subscription failed', err);
                setLoadError(true);
            }
        );
        return () => unsub();
    }, [orgId, quoteId]);

    // `truncated` is true when the window is full, so a consumer that filters
    // knows older entries of its type may exist beyond it.
    return { entries, loadError, truncated: entries.length >= MAX_ROWS };
}
